import { PlusOutlined, UserOutlined, CheckCircleOutlined, StopOutlined, DisconnectOutlined, EyeOutlined, ClockCircleOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Table, Modal, message, Form, Input, Switch, Select, Card, Statistic, Row, Col, Tooltip, Popconfirm, Tag, Typography, Tabs, Divider, Space, InputNumber } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import { customerApi } from '../services/api';
import { officeApi, warehouseApi } from '../services/entityApi';
import { roleApi, Role } from '../services/roleApi';
import { salespersonApi } from '../services/salespersonApi';
import { userApi, User, CreateUserRequest, UpdateUserRequest, UserSession, LockUserRequest } from '../services/userApi';
import userGrants, { OfficeGrant, WarehouseGrant, SalesRegionGrant, SalespersonGrant } from '../services/userGrants';

const Users: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [selectedUserForSessions, setSelectedUserForSessions] = useState<User | null>(null);
  const [form] = Form.useForm<CreateUserRequest>();
  // Grants modal state
  const [isGrantsModalOpen, setIsGrantsModalOpen] = useState(false);
  const [selectedUserForGrants, setSelectedUserForGrants] = useState<User | null>(null);
  const [officeGrants, setOfficeGrants] = useState<OfficeGrant[]>([]);
  const [warehouseGrants, setWarehouseGrants] = useState<WarehouseGrant[]>([]);
  const [salesRegionGrants, setSalesRegionGrants] = useState<SalesRegionGrant[]>([]);
  const [salespersonGrants, setSalespersonGrants] = useState<SalespersonGrant[]>([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [regionOptions, setRegionOptions] = useState<{ code: string; name: string }[]>([]);
  const [officeOptions, setOfficeOptions] = useState<{ id: number; code: string; name: string }[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<{ id: number; code: string; name: string; officeId?: number }[]>([]);
  const [selectedOfficeIdsToAdd, setSelectedOfficeIdsToAdd] = useState<number[]>([]);
  const [selectedWarehouseIdsToAdd, setSelectedWarehouseIdsToAdd] = useState<number[]>([]);
  const [salespersonOptions, setSalespersonOptions] = useState<{ code: string; name: string }[]>([]);
  const [selectedSalespersonCodesToAdd, setSelectedSalespersonCodesToAdd] = useState<string[]>([]);

  const { data: users, isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: async () => {
      const result = await userApi.getUsers();
      console.log('Users data from API:', result);
      console.log('Online users count:', result?.filter(u => u.isOnline).length || 0);
      return result;
    },
    refetchInterval: 30000, // 30 saniyede bir otomatik yenile
    refetchIntervalInBackground: true, // Arka planda da yenile
    staleTime: 10000 // 10 saniye boyunca veri taze kabul edilir
  });

  const { data: roles, isLoading: rolesLoading } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: roleApi.getRoles
  });

  const { data: userSessions, isLoading: userSessionsLoading } = useQuery<UserSession[]>({
    queryKey: ['userSessions', selectedUserForSessions?.id],
    queryFn: () => selectedUserForSessions ? userApi.getUserSessionsAdmin(selectedUserForSessions.id) : Promise.resolve([]),
    enabled: Boolean(selectedUserForSessions) && isSessionModalOpen,
    refetchInterval: 30000
  });

  const createUserMutation = useMutation({
    mutationFn: userApi.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('Kullanıcı başarıyla oluşturuldu');
      setIsModalOpen(false);
      form.resetFields();
      setSelectedUser(null);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Kullanıcı oluşturulurken bir hata oluştu');
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, user }: { id: string; user: UpdateUserRequest }) =>
      userApi.updateUser(id, user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('Kullanıcı başarıyla güncellendi');
      setIsModalOpen(false);
      form.resetFields();
      setSelectedUser(null);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Kullanıcı güncellenirken bir hata oluştu');
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: userApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('Kullanıcı başarıyla silindi');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Kullanıcı silinirken bir hata oluştu');
    }
  });

  // Session management mutations
  const terminateUserSessionMutation = useMutation({
    mutationFn: ({ userId, sessionId }: { userId: string; sessionId: string }) =>
      userApi.terminateUserSession(userId, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSessions', selectedUserForSessions?.id] });
      message.success('Oturum başarıyla sonlandırıldı');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Oturum sonlandırılırken bir hata oluştu');
    }
  });

  const terminateAllUserSessionsMutation = useMutation({
    mutationFn: (userId: string) => userApi.terminateAllUserSessions(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSessions', selectedUserForSessions?.id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('Tüm oturumlar başarıyla sonlandırıldı');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Oturumlar sonlandırılırken bir hata oluştu');
    }
  });

  const lockUserMutation = useMutation({
    mutationFn: ({ userId, request }: { userId: string; request: LockUserRequest }) => 
      userApi.lockUser(userId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('Kullanıcı başarıyla kilitlendi');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Kullanıcı kilitlenirken bir hata oluştu');
    }
  });

  const unlockUserMutation = useMutation({
    mutationFn: (userId: string) => userApi.unlockUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('Kullanıcının kilidi başarıyla açıldı');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Kullanıcının kilidi açılırken bir hata oluştu');
    }
  });

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    
    // Rol isimlerini almak için
    const roleNames = user.roles.map(roleId => {
      const role = roles?.find(r => r.id === roleId);
      return role?.name || '';
    }).filter(name => name !== '');
    
    form.setFieldsValue({
      userName: user.userName,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      roles: roleNames,  // Rol ID'leri yerine rol isimleri kullanılmalı
      isActive: user.isActive
    });
    setIsModalOpen(true);
  };

  // Roles geç yüklenirse, modal açıkken formu senkronize et
  useEffect(() => {
    if (selectedUser && isModalOpen && roles && roles.length > 0) {
      const roleNames = selectedUser.roles
        .map(roleId => roles.find(r => r.id === roleId)?.name || '')
        .filter(name => name !== '');
      form.setFieldsValue({ roles: roleNames });
    }
  }, [roles, selectedUser, isModalOpen, form]);

  const handleViewSessions = (user: User) => {
    setSelectedUserForSessions(user);
    setIsSessionModalOpen(true);
  };

  const fetchUserGrants = async (userId: string) => {
    setGrantsLoading(true);
    try {
      const [officesRes, warehousesRes, regionsRes, salespersonsRes] = await Promise.allSettled([
        userGrants.getUserOffices(userId),
        userGrants.getUserWarehouses(userId),
        userGrants.getUserSalesRegions(userId),
        userGrants.getUserSalespersons(userId)
      ]);

      const offices = officesRes.status === 'fulfilled' ? officesRes.value : [];
      const warehouses = warehousesRes.status === 'fulfilled' ? warehousesRes.value : [];
      const regions = regionsRes.status === 'fulfilled' ? regionsRes.value : [];
      const salespersons = salespersonsRes.status === 'fulfilled' ? salespersonsRes.value : [];

      setOfficeGrants(offices || []);
      setWarehouseGrants(warehouses || []);
      setSalesRegionGrants(regions || []);
      setSalespersonGrants(salespersons || []);

      if (officesRes.status === 'rejected' || warehousesRes.status === 'rejected' || regionsRes.status === 'rejected' || salespersonsRes.status === 'rejected') {
        console.warn('Bazı yetki istekleri başarısız oldu', {
          offices: officesRes.status,
          warehouses: warehousesRes.status,
          regions: regionsRes.status,
          salespersons: salespersonsRes.status
        });
      }
    } catch (e) {
      console.warn('Yetkiler alınırken beklenmeyen hata', e);
    } finally {
      setGrantsLoading(false);
    }
  };

  const openGrantsModal = async (user: User) => {
    setSelectedUserForGrants(user);
    setIsGrantsModalOpen(true);
    setGrantsLoading(true);
    try {
      await fetchUserGrants(user.id);
      // TR lokasyon hiyerarşisinden eyalet/il (bölge) listesi
      try {
        const hierarchy = await customerApi.getLocationHierarchy('TR', 'TR');
        const states = hierarchy?.states || [];
        const opts = states.map((s: any) => ({ code: s.stateCode, name: s.stateDescription }));
        setRegionOptions(opts);
      } catch (e) {
        console.warn('Bölge seçenekleri alınamadı', e);
        setRegionOptions([]);
      }
      // Ofis ve Depo seçenekleri
      try {
        const [officesResp, warehousesResp] = await Promise.all([
          officeApi.getOffices(),
          warehouseApi.getWarehouses()
        ]);
        const oData = Array.isArray(officesResp?.data) ? officesResp.data : [];
        const wData = Array.isArray(warehousesResp?.data) ? warehousesResp.data : [];
        setOfficeOptions(
          oData.map((o: any) => ({
            id: o.officeId ?? o.id ?? 0,
            code: o.officeCode ?? o.code ?? '',
            name: o.officeName ?? o.officeDescription ?? ''
          })).filter((o: any) => o.id)
        );
        setWarehouseOptions(
          wData.map((w: any) => ({
            id: w.warehouseId ?? w.id ?? 0,
            code: w.warehouseCode ?? w.code ?? '',
            name: w.warehouseName ?? w.warehouseDescription ?? '',
            officeId: w.officeId ?? w.parentOfficeId
          })).filter((w: any) => w.id)
        );
      } catch (e) {
        console.warn('Ofis/Depo seçenekleri alınamadı', e);
        setOfficeOptions([]);
        setWarehouseOptions([]);
      }
      // Satış sorumlusu seçenekleri
      try {
        const sps = await salespersonApi.getSalespersons('TR');
        setSalespersonOptions(
          (sps || []).map(sp => ({ code: sp.salespersonCode, name: sp.salespersonFirstLastName || sp.salespersonCode }))
        );
      } catch (e) {
        console.warn('Satış sorumlusu seçenekleri alınamadı', e);
        setSalespersonOptions([]);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Yetkiler yüklenemedi');
    } finally {
      setGrantsLoading(false);
    }
  };

  const formatLastActivity = (lastActivity: string) => {
    const date = new Date(lastActivity);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dakika önce`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} saat önce`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} gün önce`;
  };

  const getPageDisplayName = (url?: string) => {
    if (!url) return 'Bilinmiyor';
    
    const pathMap: { [key: string]: string } = {
      '/dashboard': 'Ana Sayfa',
      '/users': 'Kullanıcılar',
      '/profile': 'Profil',
      '/settings': 'Ayarlar',
      '/reports': 'Raporlar'
    };
    
    for (const [path, name] of Object.entries(pathMap)) {
      if (url.includes(path)) return name;
    }
    
    return url.split('/').pop() || 'Bilinmiyor';
  };

  const columns = [
    {
      title: 'Kullanıcı Adı',
      dataIndex: 'userName',
      key: 'userName',
      width: 120
    },
    {
      title: 'Ad',
      dataIndex: 'firstName',
      key: 'firstName',
      width: 100
    },
    {
      title: 'Soyad',
      dataIndex: 'lastName',
      key: 'lastName',
      width: 100
    },
    {
      title: 'Çevrimiçi',
      dataIndex: 'isOnline',
      key: 'isOnline',
      width: 110,
      render: (isOnline: boolean) => (
        <div className="flex items-center">
          <div className={`w-3 h-3 rounded-full mr-2 ${
            isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
          }`} />
          <span className={`text-sm font-medium ${
            isOnline ? 'text-green-600' : 'text-gray-500'
          }`}>
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </div>
      )
    },
    {
      title: 'Son Aktivite',
      key: 'lastActivity',
      width: 180,
      render: (_: any, record: User) => (
        <div className="text-sm">
          {record.isOnline && record.lastActivity ? (
            <>
              <div className="text-gray-600">
                {formatLastActivity(record.lastActivity)}
              </div>
              {record.lastPageUrl && (
                <div className="text-xs text-gray-400">
                  {getPageDisplayName(record.lastPageUrl)}
                </div>
              )}
            </>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      )
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      width: 200
    },
    {
      title: 'Roller',
      dataIndex: 'roles',
      key: 'roles',
      width: 150,
      render: (roleIds: string[]) => {
        const userRoles = roles?.filter(role => roleIds.includes(role.id)) || [];
        return userRoles.map(role => role.name).join(', ');
      }
    },
    {
      title: 'Durum',
      key: 'status',
      width: 120,
      render: (_: any, record: User) => {
        if (record.isLockedOut) {
          const lockoutEnd = record.lockoutEnd ? new Date(record.lockoutEnd) : null;
          const isLockoutActive = lockoutEnd && lockoutEnd > new Date();
          
          return (
            <div className="flex flex-col">
              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                {isLockoutActive ? 'Kilitli' : 'Aktif'}
              </span>
              {isLockoutActive && lockoutEnd && (
                <span className="text-xs text-gray-500 mt-1">
                  {Math.ceil((lockoutEnd.getTime() - Date.now()) / 60000)} dk kaldı
                </span>
              )}
            </div>
          );
        }
        
        return (
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
            record.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {record.isActive ? 'Aktif' : 'Pasif'}
          </span>
        );
      }
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 200,
      render: (_: any, record: User) => (
        <div className="flex flex-wrap gap-1">
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            Düzenle
          </Button>
          <Button 
            type="link" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handleViewSessions(record)}
          >
            Oturumlar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => openGrantsModal(record)}
          >
            Yetkiler
          </Button>
          {record.isOnline && (
            <Popconfirm
              title="Tüm oturumları sonlandır"
              description="Bu kullanıcının tüm aktif oturumlarını sonlandırmak istediğinizden emin misiniz?"
              onConfirm={() => terminateAllUserSessionsMutation.mutate(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button 
                type="link" 
                size="small" 
                danger 
                icon={<DisconnectOutlined />}
                loading={terminateAllUserSessionsMutation.isPending}
              >
                Oturumları Sonlandır
              </Button>
            </Popconfirm>
          )}
          {record.isLockedOut && record.lockoutEnd && new Date(record.lockoutEnd) > new Date() ? (
            <Popconfirm
              title="Kullanıcı kilidini aç"
              description="Bu kullanıcının kilidini açmak istediğinizden emin misiniz?"
              onConfirm={() => unlockUserMutation.mutate(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button 
                type="link" 
                size="small" 
                icon={<UnlockOutlined />}
                loading={unlockUserMutation.isPending}
              >
                Kilidi Aç
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Kullanıcıyı kilitler"
              description="Bu kullanıcıyı 30 dakika süreyle kilitlemek istediğinizden emin misiniz?"
              onConfirm={() => lockUserMutation.mutate({ 
                userId: record.id, 
                request: { lockoutDurationMinutes: 30 } 
              })}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button 
                type="link" 
                size="small" 
                danger
                icon={<LockOutlined />}
                loading={lockUserMutation.isPending}
              >
                Kilitler
              </Button>
            </Popconfirm>
          )}
          <Button type="link" size="small" danger onClick={() => deleteUserMutation.mutate(record.id)}>
            Sil
          </Button>
        </div>
      )
    }
  ];

  // Online kullanıcı istatistikleri
  const totalUsers = users?.length || 0;
  const onlineUsers = users?.filter(user => user.isOnline).length || 0;
  const offlineUsers = totalUsers - onlineUsers;
  const activeUsers = users?.filter(user => user.isActive).length || 0;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Kullanıcı Yönetimi</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setSelectedUser(null);
            setIsModalOpen(true);
          }}
        >
          Yeni Kullanıcı
        </Button>
      </div>

      {/* Kullanıcı İstatistikleri */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="Toplam Kullanıcı"
              value={totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Çevrimiçi Kullanıcı"
              value={onlineUsers}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Çevrimdışı Kullanıcı"
              value={offlineUsers}
              prefix={<StopOutlined />}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Aktif Kullanıcı"
              value={activeUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <div className="bg-white rounded-lg shadow">
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={usersLoading}
          scroll={{ x: 800 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kullanıcı`
          }}
        />
      </div>

      <Modal
        title={selectedUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setSelectedUser(null);
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values: any) => {
            // ASP.NET Identity rol isimlerini bekliyor, ID değil
            const processedValues = {
              ...values,
              roles: values.roles || [] // Rol isimlerini direkt gönder
            };
            
            console.log('Form values:', values);
            console.log('Role names:', values.roles);
            console.log('Available roles:', roles);
            console.log('Processed values:', processedValues);
            
            if (selectedUser) {
              const { password, ...updateData } = processedValues;
              // Ensure isActive is always included for update
              const updateRequest: UpdateUserRequest = {
                ...updateData,
                isActive: updateData.isActive === undefined ? true : updateData.isActive
              };
              updateUserMutation.mutate({
                id: selectedUser.id,
                user: updateRequest
              });
            } else {
              // Remove isActive from create request as backend doesn't expect it
              const { isActive, ...createData } = processedValues;
              createUserMutation.mutate(createData as CreateUserRequest);
            }
          }}
          initialValues={{
            userName: '',
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            roles: [],
            isActive: true
          }}
        >
          <Form.Item
            label="Kullanıcı Adı"
            name="userName"
            rules={[{ required: true, message: 'Kullanıcı adı gerekli' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Ad"
            name="firstName"
            rules={[{ required: true, message: 'Ad gerekli' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Soyad"
            name="lastName"
            rules={[{ required: true, message: 'Soyad gerekli' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="E-posta"
            name="email"
            rules={[
              { required: true, message: 'E-posta gerekli' },
              { type: 'email', message: 'Geçerli bir e-posta adresi girin' }
            ]}
          >
            <Input />
          </Form.Item>

          {!selectedUser && (
            <Form.Item
              label="Şifre"
              name="password"
              rules={[{ required: true, message: 'Şifre gerekli' }]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item
            label="Roller"
            name="roles"
            rules={[{ required: true, message: 'En az bir rol seçin' }]}
          >
            <Select
              mode="multiple"
              loading={rolesLoading}
              placeholder="Roller seçin"
              options={roles?.map(role => ({
                label: role.name,
                value: role.name  // role.id yerine role.name kullanılmalı
              }))}
            />
          </Form.Item>

          <Form.Item
            name="isActive"
            valuePropName="checked"
          >
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <Button className="mr-2" onClick={() => {
              setIsModalOpen(false);
              form.resetFields();
              setSelectedUser(null);
            }}>
              İptal
            </Button>
            <Button type="primary" htmlType="submit">
              {selectedUser ? 'Güncelle' : 'Oluştur'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Grants Management Modal */}
      <Modal
        title={selectedUserForGrants ? `${selectedUserForGrants.firstName} ${selectedUserForGrants.lastName} - Yetkiler` : 'Yetkiler'}
        open={isGrantsModalOpen}
        onCancel={() => {
          setIsGrantsModalOpen(false);
          setSelectedUserForGrants(null);
        }}
        width={900}
        footer={null}
      >
        <Tabs
          defaultActiveKey="offices"
          items={[
            {
              key: 'offices',
              label: 'Ofisler',
              children: (
                <div>
                  <Table
                    size="small"
                    rowKey={(r) => `${r.officeId}`}
                    loading={grantsLoading}
                    dataSource={officeGrants}
                    pagination={false}
                    columns={[
                      { title: 'OfficeId', dataIndex: 'officeId', key: 'officeId' },
                      { title: 'Kod', dataIndex: 'officeCode', key: 'officeCode' },
                      { title: 'Ad', dataIndex: 'officeName', key: 'officeName' },
                      {
                        title: 'Varsayılan',
                        key: 'isDefault',
                        render: (_: any, rec: OfficeGrant, idx: number) => (
                          <Switch
                            checked={rec.isDefault}
                            onChange={(checked) => {
                              setOfficeGrants(prev => prev.map((x, i) => ({ ...x, isDefault: i === idx ? checked : false })));
                            }}
                          />
                        )
                      },
                      {
                        title: 'İşlem',
                        key: 'action',
                        render: (_: any, rec: OfficeGrant, idx: number) => (
                          <Button danger type="link" onClick={() => setOfficeGrants(prev => prev.filter((_, i) => i !== idx))}>Sil</Button>
                        )
                      }
                    ]}
                  />
                  <Divider />
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>Ofis ekle</div>
                    <Select
                      mode="multiple"
                      style={{ minWidth: 360, maxWidth: 520 }}
                      placeholder="Ofis seçiniz"
                      value={selectedOfficeIdsToAdd}
                      onChange={(vals) => setSelectedOfficeIdsToAdd(vals as number[])}
                      options={officeOptions.map(o => ({ label: `${o.code} - ${o.name}`, value: o.id }))}
                    />
                    <Space>
                      <Button
                        onClick={() => {
                          if (!selectedOfficeIdsToAdd?.length) return message.info('Ofis seçiniz');
                          const toAdd = selectedOfficeIdsToAdd.filter(id => !officeGrants.some(g => g.officeId === id));
                          if (toAdd.length === 0) return message.info('Seçilen ofisler zaten listede');
                          setOfficeGrants(prev => [
                            ...prev,
                            ...toAdd.map((id, index) => ({
                              officeId: id,
                              isDefault: prev.length === 0 && index === 0
                            }))
                          ]);
                          setSelectedOfficeIdsToAdd([]);
                        }}
                      >Ekle</Button>
                      <Button
                        type="primary"
                        loading={grantsLoading}
                        onClick={async () => {
                          if (!selectedUserForGrants) return;
                          const defaults = officeGrants.filter(o => o.isDefault);
                          if (defaults.length > 1) return message.error('En fazla bir varsayılan ofis seçilebilir');
                          try {
                            await userGrants.putUserOffices(selectedUserForGrants.id, officeGrants);
                            await fetchUserGrants(selectedUserForGrants.id);
                            message.success('Ofis yetkileri kaydedildi');
                          } catch (e: any) {
                            message.error(e?.response?.data?.message || 'Ofis yetkileri kaydedilemedi');
                          }
                        }}
                      >Kaydet</Button>
                    </Space>
                  </Space>
                </div>
              )
            },
            {
              key: 'warehouses',
              label: 'Depolar',
              children: (
                <div>
                  <Table
                    size="small"
                    rowKey={(r) => `${r.warehouseId}`}
                    loading={grantsLoading}
                    dataSource={warehouseGrants}
                    pagination={false}
                    columns={[
                      { title: 'WarehouseId', dataIndex: 'warehouseId', key: 'warehouseId' },
                      { title: 'Kod', dataIndex: 'warehouseCode', key: 'warehouseCode' },
                      { title: 'Ad', dataIndex: 'warehouseName', key: 'warehouseName' },
                      { title: 'OfficeId', dataIndex: 'officeId', key: 'officeId' },
                      {
                        title: 'Varsayılan',
                        key: 'isDefault',
                        render: (_: any, rec: WarehouseGrant, idx: number) => (
                          <Switch
                            checked={rec.isDefault}
                            onChange={(checked) => {
                              setWarehouseGrants(prev => prev.map((x, i) => ({ ...x, isDefault: i === idx ? checked : false })));
                            }}
                          />
                        )
                      },
                      {
                        title: 'İşlem',
                        key: 'action',
                        render: (_: any, rec: WarehouseGrant, idx: number) => (
                          <Button danger type="link" onClick={() => setWarehouseGrants(prev => prev.filter((_, i) => i !== idx))}>Sil</Button>
                        )
                      }
                    ]}
                  />
                  <Divider />
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>Depo ekle</div>
                    <Select
                      mode="multiple"
                      style={{ minWidth: 360, maxWidth: 520 }}
                      placeholder="Depo seçiniz"
                      value={selectedWarehouseIdsToAdd}
                      onChange={(vals) => setSelectedWarehouseIdsToAdd(vals as number[])}
                      options={warehouseOptions.map(w => ({ label: `${w.code} - ${w.name}`, value: w.id }))}
                    />
                    <Space>
                      <Button
                        onClick={() => {
                          if (!selectedWarehouseIdsToAdd?.length) return message.info('Depo seçiniz');
                          const toAdd = selectedWarehouseIdsToAdd.filter(id => !warehouseGrants.some(g => g.warehouseId === id));
                          if (toAdd.length === 0) return message.info('Seçilen depolar zaten listede');
                          setWarehouseGrants(prev => [
                            ...prev,
                            ...toAdd.map((id, index) => ({
                              warehouseId: id,
                              isDefault: prev.length === 0 && index === 0
                            }))
                          ]);
                          setSelectedWarehouseIdsToAdd([]);
                        }}
                      >Ekle</Button>
                      <Button
                        type="primary"
                        loading={grantsLoading}
                        onClick={async () => {
                          if (!selectedUserForGrants) return;
                          const defaults = warehouseGrants.filter(w => w.isDefault);
                          if (defaults.length > 1) return message.error('En fazla bir varsayılan depo seçilebilir');
                          try {
                            await userGrants.putUserWarehouses(selectedUserForGrants.id, warehouseGrants);
                            await fetchUserGrants(selectedUserForGrants.id);
                            message.success('Depo yetkileri kaydedildi');
                          } catch (e: any) {
                            message.error(e?.response?.data?.message || 'Depo yetkileri kaydedilemedi');
                          }
                        }}
                      >Kaydet</Button>
                    </Space>
                  </Space>
                </div>
              )
            },
            {
              key: 'salespersons',
              label: 'Satış Sorumluları',
              children: (
                <div>
                  <Table
                    size="small"
                    rowKey={(r) => `${r.salespersonCode}`}
                    loading={grantsLoading}
                    dataSource={salespersonGrants}
                    pagination={false}
                    columns={[
                      { title: 'Kod', dataIndex: 'salespersonCode', key: 'salespersonCode' },
                      { title: 'Ad', dataIndex: 'salespersonName', key: 'salespersonName', render: (v: any, rec: SalespersonGrant) => v || (salespersonOptions.find(s => s.code === rec.salespersonCode)?.name) },
                      {
                        title: 'Varsayılan',
                        key: 'isDefault',
                        render: (_: any, rec: SalespersonGrant, idx: number) => (
                          <Switch
                            checked={rec.isDefault}
                            onChange={(checked) => {
                              setSalespersonGrants(prev => prev.map((x, i) => ({ ...x, isDefault: i === idx ? checked : false })));
                            }}
                          />
                        )
                      },
                      {
                        title: 'İşlem',
                        key: 'action',
                        render: (_: any, rec: SalespersonGrant, idx: number) => (
                          <Button danger type="link" onClick={() => setSalespersonGrants(prev => prev.filter((_, i) => i !== idx))}>Sil</Button>
                        )
                      }
                    ]}
                  />
                  <Divider />
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      Satış sorumlusu ekle
                      <div style={{ fontSize: 12, color: '#999' }}>
                        Not: Eğer arka uçta ilgili satış sorumlusu yetki kaydetme endpoint'i bulunmuyorsa (404),
                        kaydetme işlemi desteklenmez. Bu durumda sadece listeleme ve ekleme denemeleri yapılır.
                      </div>
                    </div>
                    <Select
                      mode="multiple"
                      style={{ minWidth: 360, maxWidth: 520 }}
                      placeholder="Satış sorumlusu seçiniz"
                      value={selectedSalespersonCodesToAdd}
                      onChange={(vals) => setSelectedSalespersonCodesToAdd(vals as string[])}
                      options={salespersonOptions.map(s => ({ label: `${s.code} - ${s.name}`, value: s.code }))}
                      showSearch
                      optionFilterProp="label"
                    />
                    <Space>
                      <Button
                        onClick={() => {
                          if (!selectedSalespersonCodesToAdd?.length) return message.info('Satış sorumlusu seçiniz');
                          const toAdd = selectedSalespersonCodesToAdd.filter(code => !salespersonGrants.some(g => g.salespersonCode === code));
                          if (toAdd.length === 0) return message.info('Seçilen satış sorumluları zaten listede');
                          setSalespersonGrants(prev => [
                            ...prev,
                            ...toAdd.map((code, index) => ({
                              salespersonCode: code,
                              salespersonName: salespersonOptions.find(s => s.code === code)?.name,
                              isDefault: prev.length === 0 && index === 0
                            }))
                          ]);
                          setSelectedSalespersonCodesToAdd([]);
                        }}
                      >Ekle</Button>
                      <Button
                        type="primary"
                        loading={grantsLoading}
                        onClick={async () => {
                          if (!selectedUserForGrants) return;
                          const defaults = salespersonGrants.filter(s => s.isDefault);
                          if (defaults.length > 1) return message.error('En fazla bir varsayılan satış sorumlusu seçilebilir');
                          try {
                            await userGrants.putUserSalespersons(selectedUserForGrants.id, salespersonGrants);
                            await fetchUserGrants(selectedUserForGrants.id);
                            message.success('Satış sorumlusu yetkileri kaydedildi');
                          } catch (e: any) {
                            if (e?.code === 'UNSUPPORTED_404' || e?.isUnsupported) {
                              message.warning('Satış sorumlusu yetkileri kaydedilemedi: Backend endpoint bulunamadı (404). Bu özellik şu an desteklenmiyor.');
                            } else if (e?.response?.status === 404) {
                              message.warning('Satış sorumlusu yetkileri kaydedilemedi: Backend endpoint bulunamadı (404).');
                            } else {
                              message.error(e?.response?.data?.message || 'Satış sorumlusu yetkileri kaydedilemedi');
                            }
                          }
                        }}
                      >Kaydet</Button>
                    </Space>
                  </Space>
                </div>
              )
            },
            {
              key: 'salesRegions',
              label: 'Satış Bölgeleri',
              children: (
                <div>
                  <div className="mb-2">Bölgeler (çoklu seçim)</div>
                  <Select
                    mode="multiple"
                    style={{ width: '100%', maxWidth: 520 }}
                    placeholder="Bölge seçiniz"
                    value={(salesRegionGrants || []).map(g => g.stateCode || g.countryCode).filter(Boolean) as string[]}
                    options={regionOptions.map(r => ({ label: r.name, value: r.code }))}
                    onChange={(values) => {
                      // values: string[] (state codes)
                      const next: SalesRegionGrant[] = (values as string[]).map(code => {
                        const name = regionOptions.find(r => r.code === code)?.name;
                        return { countryCode: 'TR', stateCode: code, regionName: name } as SalesRegionGrant;
                      });
                      setSalesRegionGrants(next);
                    }}
                  />
                  <Divider />
                  <Space>
                    <Button
                      type="primary"
                      loading={grantsLoading}
                      onClick={async () => {
                        if (!selectedUserForGrants) return;
                        try {
                          await userGrants.putUserSalesRegions(selectedUserForGrants.id, salesRegionGrants);
                          await fetchUserGrants(selectedUserForGrants.id);
                          message.success('Satış bölgesi yetkileri kaydedildi');
                        } catch (e: any) {
                          message.error(e?.response?.data?.message || 'Satış bölgesi yetkileri kaydedilemedi');
                        }
                      }}
                    >Kaydet</Button>
                  </Space>
                </div>
              )
            }
          ]}
        />
      </Modal>

      {/* Session Management Modal */}
      <Modal
        title={`${selectedUserForSessions?.firstName} ${selectedUserForSessions?.lastName} - Oturum Yönetimi`}
        open={isSessionModalOpen}
        onCancel={() => {
          setIsSessionModalOpen(false);
          setSelectedUserForSessions(null);
        }}
        width={800}
        footer={[
          <Button key="close" onClick={() => {
            setIsSessionModalOpen(false);
            setSelectedUserForSessions(null);
          }}>
            Kapat
          </Button>,
          selectedUserForSessions && (
            <Popconfirm
              key="terminate-all"
              title="Tüm oturumları sonlandır"
              description="Bu kullanıcının tüm aktif oturumlarını sonlandırmak istediğinizden emin misiniz?"
              onConfirm={() => {
                terminateAllUserSessionsMutation.mutate(selectedUserForSessions.id);
                setIsSessionModalOpen(false);
                setSelectedUserForSessions(null);
              }}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button 
                type="primary" 
                danger 
                icon={<DisconnectOutlined />}
                loading={terminateAllUserSessionsMutation.isPending}
              >
                Tüm Oturumları Sonlandır
              </Button>
            </Popconfirm>
          )
        ]}
      >
        <div className="space-y-4">
          {userSessionsLoading ? (
            <div className="text-center py-8">
              <Typography.Text>Oturumlar yükleniyor...</Typography.Text>
            </div>
          ) : userSessions && userSessions.length > 0 ? (
            <div className="space-y-3">
              {userSessions.map((session) => (
                <Card key={session.id} size="small" className="border">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Tag color={session.isActive ? 'green' : 'red'}>
                          {session.isActive ? 'Aktif' : 'Pasif'}
                        </Tag>
                        {session.isCurrent && (
                          <Tag color="blue">Mevcut Oturum</Tag>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <Typography.Text strong>Cihaz:</Typography.Text>
                          <br />
                          <Typography.Text>{session.deviceInfo}</Typography.Text>
                        </div>
                        <div>
                          <Typography.Text strong>IP Adresi:</Typography.Text>
                          <br />
                          <Typography.Text>{session.ipAddress}</Typography.Text>
                        </div>
                        <div>
                          <Typography.Text strong>Son Aktivite:</Typography.Text>
                          <br />
                          <Typography.Text>
                            <ClockCircleOutlined className="mr-1" />
                            {formatLastActivity(session.lastActivity)}
                          </Typography.Text>
                        </div>
                        <div>
                          <Typography.Text strong>Son Sayfa:</Typography.Text>
                          <br />
                          <Typography.Text>
                            {session.lastPageUrl ? getPageDisplayName(session.lastPageUrl) : 'Bilinmiyor'}
                          </Typography.Text>
                        </div>
                        <div>
                          <Typography.Text strong>Oturum Başlangıcı:</Typography.Text>
                          <br />
                          <Typography.Text>
                            {new Date(session.createdAt).toLocaleString('tr-TR')}
                          </Typography.Text>
                        </div>
                      </div>
                    </div>
                    
                    {session.isActive && !session.isCurrent && selectedUserForSessions && (
                      <Popconfirm
                        title="Oturumu sonlandır"
                        description="Bu oturumu sonlandırmak istediğinizden emin misiniz?"
                        onConfirm={() => terminateUserSessionMutation.mutate({
                          userId: selectedUserForSessions.id,
                          sessionId: session.id
                        })}
                        okText="Evet"
                        cancelText="Hayır"
                      >
                        <Button 
                          size="small" 
                          danger 
                          icon={<DisconnectOutlined />}
                          loading={terminateUserSessionMutation.isPending}
                        >
                          Sonlandır
                        </Button>
                      </Popconfirm>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Typography.Text type="secondary">
                Bu kullanıcının aktif oturumu bulunmuyor.
              </Typography.Text>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Users;
